/**
 * copy-field — the write channel behind one copy field's own Languages
 * trigger: the operator opened `<localized-text>` beside a single field and
 * either corrected a version by hand, or asked for the versions to be
 * written again.
 *
 * SEPARATE FROM `localize.state.ts`'s accept channel on purpose. That one
 * carries a whole event's accepted draft — the description and every ticket type —
 * in ONE batch, because a catalogue localized in part is the failure it
 * exists to refuse. A hand-edit is the opposite act: one field, one map, and
 * nothing else on either document may move. Its ticket-type entry carries
 * `nameByLocale` and `descriptionByLocale` together, so routing a name-only
 * correction through it would re-write the description map the operator
 * never opened.
 *
 * `copy-field.sync.ts` (web-side) is the sole subscriber, patching that one
 * field and reporting back by requestId. A null map is the cleared field:
 * every version was emptied, and an entry is a string or it is not there.
 */
import type { LocalizedText } from "@momo/i18n";
import {
	defer,
	EMPTY,
	filter,
	map,
	merge,
	type Observable,
	Subject,
} from "rxjs";

/**
 * Which stored map the gesture belongs to. The event's description needs no second
 * id; a ticket type's two fields each need the row's own — and the shape
 * refuses the combinations that do not exist (an event has no name map of
 * its own; `Event.name` is not localized).
 *
 * The two gestures are separate variants because they carry different
 * payloads and land on different keys: correcting writes the map the
 * operator edited, asking writes nothing but the field's status, and a
 * gesture that could carry both would let one write the other's key.
 */
export type CopyFieldIntent = {
	readonly eventId: string;
} & (
	| {
			readonly gesture: "correct";
			/** The corrected map, or null for a field whose every version was emptied. */
			readonly byLocale: LocalizedText | null;
	  }
	| { readonly gesture: "translate" }
) &
	(
		| { readonly target: "event-description" }
		| {
				readonly target: "type-name" | "type-description";
				readonly ticketTypeId: string;
		  }
	);

export type CopyFieldAction = CopyFieldIntent & {
	readonly requestId: string;
};
/** The two gestures apart, for the seams that can only serve one of them. */
export type CopyFieldCorrection = Extract<
	CopyFieldAction,
	{ gesture: "correct" }
>;
export type CopyFieldPassRequest = Extract<
	CopyFieldAction,
	{ gesture: "translate" }
>;
export const copyFieldAction$ = new Subject<CopyFieldAction>();

export type CopyFieldResult = {
	readonly requestId: string;
	readonly ok: boolean;
};
export const copyFieldResult$ = new Subject<CopyFieldResult>();

/**
 * Raises one gesture on the channel and emits the reconciler's own answer to
 * it. The correlation id is minted here because it is the only thing every
 * caller would otherwise mint identically, and because it must reach the
 * result filter before the action goes out: both Subjects are hot, so an
 * answer the reconciler gives synchronously is spoken to nobody unless the
 * filter is already subscribed — which is what the merge order below buys.
 *
 * The caller owns the lifetime: this never completes on its own, so it is
 * piped through the mounting component's own teardown.
 */
export function copyFieldSettled$(
	intent: CopyFieldIntent,
): Observable<boolean> {
	return defer(() => {
		const requestId = crypto.randomUUID();
		return merge(
			copyFieldResult$.pipe(
				filter((result) => result.requestId === requestId),
				map((result) => result.ok),
			),
			defer(() => {
				copyFieldAction$.next({ ...intent, requestId });
				return EMPTY;
			}),
		);
	});
}
